import React, { Suspense, lazy } from "react";
import { Provider } from "react-redux";
import { store } from "../../configurations";
import ReactDOM from "react-dom";
import Dialog from "../../components/Dialog/Dialog";
import Spinner from "../../components/Spinner/LoadingSpinner";
import DropdownOptions from "../../components/Dropdown/DropdownOptions";
import { iTradeEnv } from "@/configurations/app-configs/iTrade.env";

import LoadingLayer from "@/components/Spinner/LoadingLayer";
import { LOADING_TYPE } from "@/constants/app";

const original = {
  placeOrderDialog: lazy(() =>
    import("../../components/PlaceOrder/PlaceOrderDialog")
  ),
  orderConfirm: lazy(() =>
    import("../../components/OrderConfirm/OrderConfirm")
  ),
  applyPlan: lazy(() => import("../../components/@app/IPO/ApplyPlan")),
  allotmentResultEnquiry: lazy(() =>
    import("../../components/@app/IPO/AllotmentResultEnquiry")
  ),
  orderCancel: lazy(() => import("../../components/OrderDialog/OrderCancel")),
  orderModify: lazy(() => import("../../components/OrderDialog/OrderModify")),
  orderJournalDetail: lazy(() =>
    import("../../components/OrderDialog/OrderJournalDetail")
  ),
  changePasswordDialog: lazy(() =>
    import(
      "../../components/Authentication/ChangePassword/ChangePasswordDialog"
    )
  ),
  orderFeesDialog: lazy(() =>
    import("../../components/OrderDialog/OrderFeesDialog")
  ),
};

const rewrites = {
  placeOrderDialog: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/PlaceOrder/PlaceOrderDialog`
    )
  ),
  orderConfirm: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderConfirm/OrderConfirm`
    )
  ),
  applyPlan: lazy(() =>
    import(`@/companys/${iTradeEnv.company}/components/@app/IPO/ApplyPlan`)
  ),
  allotmentResultEnquiry: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/@app/IPO/AllotmentResultEnquiry`
    )
  ),
  orderCancel: lazy(() =>
    import(`@/companys/${iTradeEnv.company}/components/OrderDialog/OrderCancel`)
  ),
  orderModify: lazy(() =>
    import(`@/companys/${iTradeEnv.company}/components/OrderDialog/OrderModify`)
  ),
  orderJournalDetail: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderDialog/OrderJournalDetail`
    )
  ),
  changePasswordDialog: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/Authentication/ChangePassword/ChangePasswordDialog`
    )
  ),
  orderFeesDialog: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderDialog/OrderFeesDialog'`
    )
  ),
};

const ActivationDialog = lazy(() =>
  import("../../components/Authentication/Activation/Activation")
);
const ModalsContent = lazy(() =>
  import("../../components/Dialog/ModalsContent")
);
const PersonalSettingDialog = lazy(() =>
  import("../../components/PersonalSetting/PersonalSetting")
);
const FundManagementActionDialog = lazy(() =>
  import("../../components/MyAccount/FundManagement/FundManagementActionDialog")
);
const PlaceOrderDialog = iTradeEnv?.actions?.app?.place_order
  ? rewrites.placeOrderDialog
  : original.placeOrderDialog;
const OrderConfirm = iTradeEnv?.actions?.app?.order_confirm
  ? rewrites.orderConfirm
  : original.orderConfirm;
const ApplyPlan = iTradeEnv?.pages?.ipo?.ctrl?.detail
  ? rewrites.applyPlan
  : original.applyPlan;
const MarginApply = lazy(() => import("../../components/@app/IPO/MarginApply"));
const AllotmentResultEnquiry = iTradeEnv?.actions?.ipo?.allotment_result_enquiry
  ? rewrites.allotmentResultEnquiry
  : original.allotmentResultEnquiry;
const CADetail = lazy(() =>
  import("../../components/@app/CorpAction/CorporateActionDetail")
);
const OrderCancel = iTradeEnv?.actions?.app?.order_confirm
  ? rewrites.orderCancel
  : original.orderCancel;
const OrderModify = iTradeEnv?.actions?.app?.order_confirm
  ? rewrites.orderModify
  : original.orderModify;
const OrderJournalDetail = iTradeEnv?.actions?.app?.order_confirm
  ? rewrites.orderJournalDetail
  : original.orderJournalDetail;
const OTPDialog = lazy(() => import("../../components/OTP/OTPDialog"));
const WatchListDialog = lazy(() =>
  import("../../components/Watchlist/WatchlistDialog")
);
const WatchListGroupDialog = lazy(() =>
  import("../../components/Watchlist/WatchListGroupDialog")
);
const WatchlistContextMenu = lazy(() =>
  import(`@/components/Watchlist/WatchlistContextMenu`)
);
const NewsDetailDialog = lazy(() =>
  import("../../components/News/NewsDetailDialog")
);
const AAStockDisclaimer = lazy(() =>
  import("../../components/Watchlist/AAStockDisclaimer")
);
const SessionLogoutDialog = lazy(() =>
  import("../../components/Dialog/SessionLogoutDialog")
);
const ChangePasswordDialog = iTradeEnv?.pages?.trade?.ctrl?.personalsetting
  ? rewrites.changePasswordDialog
  : original.changePasswordDialog;
const FundOrderCancelDialog = lazy(() =>
  import("../../components/FundDialog/FundOrderCancelDialog")
);
const FundOrderModifyDialog = lazy(() =>
  import("../../components/FundDialog/FundOrderModifyDialog")
);
const CashDWResultDialog = lazy(() =>
  import("../../components/FundManagement/CashDWResultDialog")
);
const FundOrderResultDialog = lazy(() =>
  import("../../components/FundDialog/FundOrderResultDialog")
);
// const ImportantNotesDialog = lazy(() => import('../../components/Authentication/ImportantNotes/ImportantNotesDialog.js'));
const SelfDefinedUserIdDialog = lazy(() =>
  import(
    "../../companys/scb/components/Authentication/SelfDefinedUserId/SelfDefinedUserIdDialog"
  )
);
const ApplySelfDefinedUserIDDialog = lazy(() =>
  import(
    "../../companys/scb/components/Authentication/ApplySelfDefinedUserID/ApplySelfDefinedUserIDDialog"
  )
);
const OrderFeesDialog = iTradeEnv?.action?.app?.order_confirm
  ? rewrites.orderFeesDialog
  : original.orderFeesDialog;
// const OrderFeesDialog = lazy(() => import('../../components/OrderDialog/OrderFeesDialog'));
const EMessageDialog = lazy(() =>
  import("../../companys/scb/components/EMessage/EMessageDialog")
);
const ImportantNotesDialog = lazy(() =>
  import(
    `@/companys/${iTradeEnv.company}/components/Authentication/ImportantNotes/ImportantNotesDialog`
  )
);
const PrePostDialog = lazy(() =>
  import("../../components/Instrument/PrePostDialog")
);
const TriggerOrderMsgDialog = lazy(() =>
  import("../../companys/scb/components/Instrument/TriggerOrderMsgDialog")
);
const AssetsDetailsDialog = lazy(() =>
  import("../../companys/ttl/components/@app/CashAccount/AssetsDetails")
);
const ColDefChoose = lazy(() =>
  import(`@/components/DataTable/components/ColDefChoose`)
);
const SCBDisclaimerDialog = lazy(() =>
  import("@/companys/scb/components/DisclaimerDialog/DisclaimerDialog")
);
const WatchlistPrompt = lazy(() =>
  import("../../components/Watchlist/WatchlistPrompt")
);
const FinancialsDetailDialog = lazy(() =>
  import(
    "@/companys/ttl/components/DetailCenter/DetailPage/FinancialsDetailDialog"
  )
);
const CommentReportDialog = lazy(() =>
  import(
    "@/companys/ttl/components/DetailCenter/DetailPage/CommentReportDialog"
  )
);
const CommentPostDialog = lazy(() =>
  import("@/companys/ttl/components/DetailCenter/DetailPage/CommentPostDialog")
);
const CommentDetailDialog = lazy(() =>
  import(
    "@/companys/ttl/components/DetailCenter/DetailPage/CommentDetailDialog"
  )
);
const PositionProportionDialog = lazy(() =>
  import(
    "@/companys/ttl/components/MyAccount/PositionProportion/PositionProportionDialog"
  )
);
const WatchlistPromptMobile = lazy(() =>
  import("../../components/Watchlist/WatchlistPrompt.mobile")
);
const MarketSetDialog = lazy(() =>
  import("@/companys/ttl/components/Instrument/MarketSetDialog")
);
const MarketSetconfirmDialog = lazy(() =>
  import("@/companys/ttl/components/Instrument/MarketSetconfirmDialog")
);
const MarketAddWatchlistDialog = lazy(() =>
  import("@/companys/ttl/components/Instrument/MarketAddWatchlistDialog")
);
const Chart = lazy(() =>
  import("@/companys/ttl/components/DetailCenter/DetailPage/Chart")
);
const VirtualTradingPlaceOrderDialog = lazy(() =>
  import(
    "@/components/VirtualTrading/VirtualTradingDetail/VirtualTradingStock/VirtualTradingPlaceOrderDialog"
  )
);
const MarketAddGroupDialog = lazy(() =>
  import("@/companys/ttl/components/Instrument/MarketAddGroupDialog.mobile")
);
const BankAccountSetting = lazy(() =>
  import("@/components/PersonalSetting/BankAccountSetting.mobile")
);
const BankSettingResult = lazy(() =>
  import("@/components/PersonalSetting/BankSettingResult")
);

const OTPverificationDialog = lazy(() => import("@/components/OTP/OTPverificationDialog"));
const EDDAccountDialog = lazy(() => import("@/components/PersonalSetting/EDDAAccount"));

const onClose = (element, callback = () => {}) => {
  ReactDOM.unmountComponentAtNode(element);
  document.body.removeChild(element);
  callback();
};

const createChild = () => {
  let child = document.createElement("div");
  document.body.appendChild(child);
  return child;
};

const removeChild = (child, callback = () => {}) => {
  ReactDOM.unmountComponentAtNode(child);
  document.body.removeChild(child);
};

export const openDropdown = (
  options,
  position,
  textCenter,
  defaultTheme,
  callback,
  DropdownStyle
) => {
  let dest = createChild();

  const onClose = (value = "") => {
    callback(value);
    removeChild(dest);
  };
  ReactDOM.render(
    <Provider store={store}>
      <DropdownOptions
        defaultTheme={defaultTheme}
        options={options}
        position={position}
        textCenter={textCenter}
        onClose={(value) => onClose(value)}
        DropdownStyle={DropdownStyle}
      />
    </Provider>,
    dest
  );
};

// Create Dialog with Activation Account
export const openActivationDialog = (inputData, callback) => {
  let dest = createChild();

  const onClose = (value) => {
    if (value) callback(value);
    removeChild(dest);
  };

  ReactDOM.render(
    <AppDialog onClose={() => onClose()}>
      <ActivationDialog
        title={"activation_title"}
        onClose={() => onClose(true)}
      />
    </AppDialog>,
    dest
  );
};

// Create Common Dialog
export const openModals = (inputData, callback) => {
  let dest = createChild();
  const style = {
    width: "472",
    height: "176",
    loadstyle: (
      <>
        <rect x="20" y="40" rx="4" ry="4" width="140" height="20" />
        <rect x="150" y="120" rx="4" ry="4" width="80" height="20" />
        <rect x="250" y="120" rx="4" ry="4" width="80" height="20" />
      </>
    ),
  };
  const onClose = (value) => {
    if (value) callback(value);
    removeChild(dest);
  };
  ReactDOM.render(
    <AppDialog
      style={inputData.style}
      onClose={() => onClose()}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <ModalsContent
        title={inputData.title}
        onActions={(e) => onClose(e)}
        {...inputData}
      />
    </AppDialog>,
    dest
  );
};

class AppDialog extends React.Component {
  render() {
    const {
      children,
      disableBackgroundColor = false,
      errorCode,
      style = {},
      dialogStyle = {},
      loadStyle = {},
    } = this.props;
    return (
      <Provider store={store}>
        <Dialog
          isOpen={true}
          disableBackdropClick={true}
          disableBackgroundColor={disableBackgroundColor}
          title={children.props.title}
          onClose={() => this.props.onClose()}
          style={style}
          dialogStyle={dialogStyle}
          errorCode={errorCode}
        >
          <Suspense
            fallback={
              <LoadingLayer
                load={true}
                type={LOADING_TYPE.SKELETON}
                height={this.props.height}
                width={this.props.width}
                style={loadStyle}
                children={this.props.loadstyle}
              />
            }
          >
            {children}
          </Suspense>
        </Dialog>
      </Provider>
    );
  }
  componentDidMount() {
    if (document.getElementsByClassName("customer-loading-overlay")[1])
      document.getElementsByClassName(
        "customer-loading-overlay"
      )[1].style.position = "unset";
  }
}

export const openPersonalSettingDialog = (selectTab) => {
  let dest = createChild();
  const style = {
    width: "866",
    height: "446",
    loadstyle: (
      <>
        <rect x="10" y="30" rx="4" ry="4" width="140" height="20" />
        <rect x="10" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="10" y="110" rx="4" ry="4" width="140" height="20" />
      </>
    ),
  };
  const loadstylefirst = {
    width: "564",
    height: "408",
    loadstyle: (
      <>
        <rect x="10" y="10" rx="4" ry="4" width="140" height="20" />
        <rect x="10" y="50" rx="4" ry="4" width="200" height="20" />
        <rect x="10" y="80" rx="4" ry="4" width="200" height="20" />
        <rect x="10" y="130" rx="4" ry="4" width="100" height="20" />
        <rect x="40" y="170" rx="4" ry="4" width="100" height="20" />
        <rect x="180" y="170" rx="4" ry="4" width="100" height="20" />
        <rect x="300" y="380" rx="4" ry="4" width="100" height="20" />
        <rect x="450" y="380" rx="4" ry="4" width="100" height="20" />
      </>
    ),
  };
  const loadstyletwo = {
    loadstyle: (
      <>
        <rect x="10" y="30" rx="4" ry="4" width="200" height="20" />
        <rect x="10" y="80" rx="4" ry="4" width="200" height="20" />
        <rect x="10" y="130" rx="4" ry="4" width="200" height="20" />
        <rect x="10" y="180" rx="4" ry="4" width="200" height="20" />
        <rect x="10" y="230" rx="4" ry="4" width="200" height="20" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <PersonalSettingDialog
        title={"personal_setting_title"}
        onClose={() => removeChild(dest)}
        loadstylefirst={loadstylefirst}
        loadstyletwo={loadstyletwo}
        selectTab={selectTab}
      />
    </AppDialog>,
    dest
  );
};

export const openCashDialog = (i) => {
  let div = createChild();

  ReactDOM.render(
    <Provider store={store}>
      <AppDialog onClose={() => onClose()}>
        <Suspense fallback={<Spinner />}>
          <FundManagementActionDialog
            pageID={i}
            onClose={() => removeChild(div)}
          />
        </Suspense>
      </AppDialog>
    </Provider>,
    div
  );
};

export const openPlaceOrderDialog = (
  isBuy,
  data,
  isOther,
  enabledSearchInstrument = true
) => {
  let dest = createChild();
  const style = {
    width: "460",
    height: "561",
    loadstyle: (
      <>
        <rect x="180" y="20" rx="4" ry="4" width="100" height="20" />
        <rect x="30" y="60" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="100" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="140" rx="4" ry="4" width="100" height="20" />
        <rect x="30" y="180" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="220" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="260" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="300" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="340" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="380" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="420" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="480" rx="4" ry="4" width="400" height="20" />
        <rect x="30" y="520" rx="4" ry="4" width="180" height="20" />
        <rect x="240" y="520" rx="4" ry="4" width="180" height="20" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <PlaceOrderDialog
        isBuy={isBuy}
        data={data}
        isOther={isOther}
        enabledSearchInstrument={enabledSearchInstrument}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openOrderConfirm = (data, cb = () => {}) => {
  let dest = createChild();
  let hasResult = false;

  ReactDOM.render(
    <AppDialog
      onClose={() => {
        cb(hasResult);
        removeChild(dest);
      }}
    >
      <OrderConfirm
        data={data}
        title={"order_confirm_title"}
        onClose={() => removeChild(dest)}
        cb={cb}
        onHasResult={() => (hasResult = true)}
      />
    </AppDialog>,
    dest
  );
};

export const openAllotmentResultEnquiry = (
  data,
  useIpoAppliedDetailsEnquiryAPI = true
) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={600}
      height={570}
      loadstyle={
        <>
          <rect x={20} y={20} width={75} height={15}></rect>
          <rect x={470} y={20} width={120} height={15}></rect>
          <rect x={20} y={50} width={570} height={445}></rect>
          <rect x={440} y={500} width={150} height={20}></rect>
        </>
      }
    >
      <AllotmentResultEnquiry
        title={
          useIpoAppliedDetailsEnquiryAPI
            ? "ipo_applied_detail_enquiry"
            : "ipo_allotment_result_enquiry"
        }
        data={data}
        ipoCancel={false}
        useIpoAppliedDetailsEnquiryAPI={useIpoAppliedDetailsEnquiryAPI}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openOrderFeesDialog = (feesList, currencyID, feeAmt) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <OrderFeesDialog
        title={"common_commission_fees"}
        onClose={() => removeChild(dest)}
        feesList={feesList}
        currencyID={currencyID}
        feeAmt={feeAmt}
      />
    </AppDialog>,
    dest
  );
};

export const openIpoCancelDialog = (data) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={600}
      height={570}
      loadstyle={
        <>
          <rect x={20} y={20} width={75} height={15}></rect>
          <rect x={470} y={20} width={120} height={15}></rect>
          <rect x={20} y={50} width={570} height={445}></rect>
          <rect x={440} y={500} width={150} height={20}></rect>
        </>
      }
    >
      <AllotmentResultEnquiry
        title={"ipo_cancel_title"}
        data={data}
        ipoCancel={true}
        useIpoAppliedDetailsEnquiryAPI={true}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openApplyDialog = (data, mode) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={600}
      height={650}
      loadstyle={
        <>
          <rect x={230} y={32} width={135} height={20}></rect>
          <rect x={30} y={90} width={540} height={85}></rect>
          <rect x={30} y={195} width={540} height={390}></rect>
          {/* <rect x={255} y={630} width={90} height={30} ></rect> */}
        </>
      }
    >
      <ApplyPlan
        title={"ipo_apply_title"}
        data={data}
        mode={mode}
        onClose={() => removeChild(dest)}
        cb={() => cb()}
      />
    </AppDialog>,
    dest
  );
};

export const openMarginApplyDialog = (data) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <MarginApply
        // title={'ipo_apply_title'}
        data={data}
        onClose={() => removeChild(dest)}
        cb={() => cb()}
      />
    </AppDialog>,
    dest
  );
};

export const openCADetailDialog = (data, mode) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <CADetail
        data={data}
        mode={mode}
        title={"ca_detail_title"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openOrderCancelDialog = (order, callback = () => {}) => {
  let dest = createChild();
  const style = {
    width: "576",
    height: "604",
    loadstyle: (
      <>
        <rect x="25" y="30" rx="4" ry="4" width="200" height="20" />
        <rect x="30" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="110" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="150" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="190" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="230" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="270" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="310" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="350" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="390" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="430" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="470" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="510" rx="4" ry="4" width="140" height="20" />

        <rect x="420" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="110" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="150" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="190" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="230" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="270" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="310" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="350" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="390" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="430" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="470" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="510" rx="4" ry="4" width="140" height="20" />
        <rect x="320" y="560" rx="4" ry="4" width="100" height="20" />
        <rect x="460" y="560" rx="4" ry="4" width="100" height="20" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <OrderCancel
        order={order}
        title={"order_cancel_title"}
        onClose={(res) => {
          callback(res);
          removeChild(dest);
        }}
        loadstyle={style}
      />
    </AppDialog>,
    dest
  );
};

export const openOrderModifyDialog = (order, callback = () => {}) => {
  let dest = createChild();
  const style = {
    width: "580",
    height: "622",
    loadstyle: (
      <>
        <rect x="25" y="25" rx="4" ry="4" width="200" height="20" />
        <rect x="30" y="60" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="100" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="140" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="180" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="220" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="260" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="300" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="340" rx="4" ry="4" width="140" height="20" />
        <rect x="30" y="380" rx="4" ry="4" width="140" height="20" />
        <rect x="25" y="440" rx="4" ry="4" width="400" height="20" />
        <rect x="25" y="490" rx="4" ry="4" width="400" height="20" />
        <rect x="25" y="540" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="540" rx="4" ry="4" width="140" height="20" />
        <rect x="340" y="580" rx="4" ry="4" width="100" height="20" />
        <rect x="460" y="580" rx="4" ry="4" width="100" height="20" />

        <rect x="380" y="60" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="100" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="140" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="180" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="220" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="260" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="300" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="340" rx="4" ry="4" width="140" height="20" />
        <rect x="380" y="380" rx="4" ry="4" width="140" height="20" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <OrderModify
        order={order}
        title={"order_modify_title"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openOrderJournalDetailDialog = (
  order,
  dateFilter = null,
  forceDate = false,
  enabledFetchAudit = false,
  enabledFetchExecute = false
) => {
  let dest = createChild();
  const style = {
    width: "1092",
    height: "436",
    loadstyle: (
      <>
        <rect x="20" y="0" rx="4" ry="4" width="200" height="25" />
        <rect x="20" y="40" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="100" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="130" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="160" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="190" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="220" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="250" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="280" rx="4" ry="4" width="140" height="20" />
        <rect x="560" y="320" rx="4" ry="4" width="140" height="20" />
        <rect x="20" y="360" rx="4" ry="4" width="100" height="20" />
        <rect x="20" y="390" rx="4" ry="4" width="1000" height="20" />
        <rect x="20" y="420" rx="4" ry="4" width="1000" height="20" />

        <rect x="420" y="40" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="100" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="130" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="160" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="190" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="220" rx="4" ry="4" width="140" height="20" />
        <rect x="420" y="250" rx="4" ry="4" width="140" height="20" />

        <rect x="600" y="40" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="100" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="130" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="160" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="190" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="220" rx="4" ry="4" width="140" height="20" />
        <rect x="600" y="250" rx="4" ry="4" width="140" height="20" />

        <rect x="900" y="40" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="70" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="100" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="130" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="160" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="190" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="220" rx="4" ry="4" width="140" height="20" />
        <rect x="900" y="250" rx="4" ry="4" width="140" height="20" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={1112}
      height={439}
      loadstyle={style.loadstyle}
      loadStyle={{ padding: "20px" }}
    >
      <OrderJournalDetail
        order={order}
        title={"order_journal_detail_title"}
        forceDate={forceDate}
        dateFilter={dateFilter}
        enabledFetchAudit={enabledFetchAudit}
        enabledFetchExecute={enabledFetchExecute}
        onClose={() => removeChild(dest)}
        loadstyle={style}
      />
    </AppDialog>,
    dest
  );
};

export const openOTPDialog = (callback = () => {}) => {
  let dest = createChild();

  function handleClose(otpCode = "") {
    callback(otpCode);
    removeChild(dest);
  }

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <OTPDialog title="OTP Code" onClose={(otpCode) => handleClose(otpCode)} />
    </AppDialog>,
    dest
  );
};

export const openWatchListDialog = (action) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <WatchListDialog
        title={"common_{0}_watchlist".format(action)}
        action={action}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openNewsDetailDialog = (data, id, title = "common_news") => {
  let dest = createChild();
  const style = {
    width: "1000px",
    height: "600px",
    loadstyle: (
      <>
        <rect x="0" y="0" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="85" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="170" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="255" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="340" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="425" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="510" rx="4" ry="4" width="200" height="80" />
        <rect x="0" y="595" rx="4" ry="4" width="200" height="80" />
        <rect x="230" y="5" rx="4" ry="4" width="790" height="595" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <NewsDetailDialog
        title={title}
        data={data}
        id={id}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openAAStockDisclaimerDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <AAStockDisclaimer
        title="aastock_disclaimer_title"
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openSessionLogoutDialog = (params) => {
  const { errorCode, errorMessage, resetState } = params;
  let dest = createChild();
  const style = {
    width: "470",
    height: "178",
    loadstyle: (
      <>
        <rect x="30" y="35" rx="4" ry="4" width="200" height="20" />
        <rect x="190" y="120" rx="4" ry="4" width="100" height="20" />
      </>
    ),
  };

  function closeDialog() {
    if (resetState && typeof resetState === "function") {
      resetState();
    }
  }

  ReactDOM.render(
    <AppDialog
      onClose={() => closeDialog()}
      errorCode={errorCode}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <SessionLogoutDialog
        title={"common_notice"}
        onClose={() => closeDialog()}
        errorCode={errorCode}
        errorMessage={errorMessage}
      />
    </AppDialog>,
    dest
  );
};
export const openFundOrderCancelDialog = (fundOrder, callback = () => {}) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <FundOrderCancelDialog
        fundOrder={fundOrder}
        title={"fund_order_cancel_title"}
        onClose={(res) => {
          callback(res);
          removeChild(dest);
        }}
      />
    </AppDialog>,
    dest
  );
};

export const openFundOrderModifyDialog = (fundOrder, callback = () => {}) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <FundOrderModifyDialog
        fundOrder={fundOrder}
        title={"fund_order_modify_title"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const opencashDWResultDialog = (
  transactionID,
  pageID,
  isModify = false
) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      {pageID === 0 ? (
        <CashDWResultDialog
          transactionID={transactionID}
          title={"fund_management_deposit_result"}
          onClose={() => removeChild(dest)}
          pageID={pageID}
          isModify={isModify}
        />
      ) : (
        <CashDWResultDialog
          transactionID={transactionID}
          title={"fund_management_withdrawal_result"}
          onClose={() => removeChild(dest)}
          pageID={pageID}
          isModify={isModify}
        />
      )}
    </AppDialog>,
    dest
  );
};
export const openBankSecuritiesTransferResultDialog = (
  transactionID,
  pageID
) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <CashDWResultDialog
        transactionID={transactionID}
        title={"fund_management_bank_securities_transfer_result"}
        onClose={() => removeChild(dest)}
        pageID={pageID}
      />
    </AppDialog>,
    dest
  );
};

export const openTranSferResultDialog = (transactionID, pageID) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <CashDWResultDialog
        transactionID={transactionID}
        title={"fund_management_transfer_result"}
        onClose={() => removeChild(dest)}
        pageID={pageID}
      />
    </AppDialog>,
    dest
  );
};

export const opencashOrderResultDialog = (transactionID, pageID) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      {pageID === 0 ? (
        <FundOrderResultDialog
          transactionID={transactionID}
          title={"fund_management_deposit_cancel"}
          onClose={() => removeChild(dest)}
          pageID={pageID}
        />
      ) : (
        <FundOrderResultDialog
          transactionID={transactionID}
          title={"fund_management_withdrawal_cancel"}
          onClose={() => removeChild(dest)}
          pageID={pageID}
        />
      )}
    </AppDialog>,
    dest
  );
};

export const openChangePasswordDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <ChangePasswordDialog title={"change_password_header"} />
    </AppDialog>,
    dest
  );
};

export const openImportantNotesDialog = (data) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <ImportantNotesDialog
        title={"important_notes_title"}
        onClose={() => removeChild(dest)}
        data={data}
      />
    </AppDialog>,
    dest
  );
};

export const openEMessageDialog = (inputData, callback) => {
  let dest = createChild();

  const onClose = (value) => {
    if (value) {
      callback(value);
    } else {
      callback(true);
    }
    removeChild(dest);
  };
  ReactDOM.render(
    <AppDialog onClose={() => onClose()}>
      <EMessageDialog
        title={"delete_EMessage_title"}
        onActions={(e) => onClose(e)}
        data={inputData}
      />
    </AppDialog>,
    dest
  );
};

export const openSelfDefinedUserIdDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <SelfDefinedUserIdDialog title={"enquiry_self_defined_user_id"} />
    </AppDialog>,
    dest
  );
};

export const openApplySelfDefinedUserIdDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <ApplySelfDefinedUserIDDialog
        title={"apply_self_defined_user_id_title"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openPrePostDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <PrePostDialog
        title={"common_order_statement"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openTriggerOrderMsgDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <TriggerOrderMsgDialog
        title={"important_notes_title"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openAssetsDetails = () => {
  let dest = createChild();
  const style = {
    width: "540",
    height: "300",
    loadstyle: (
      <>
        <rect x="0" y="0" rx="4" ry="4" width="140" height="25" />
        <rect x="0" y="30" rx="4" ry="4" width="140" height="25" />
        <rect x="0" y="85" rx="4" ry="4" width="140" height="25" />
        <rect x="0" y="115" rx="4" ry="4" width="140" height="25" />
        <rect x="0" y="170" rx="4" ry="4" width="140" height="25" />
        <rect x="0" y="200" rx="4" ry="4" width="140" height="25" />
        <rect x="0" y="255" rx="4" ry="4" width="140" height="25" />
        {/* <rect x="0" y="285" rx="4" ry="4" width="140" height="25" />    */}
        {/* <rect x="0" y="340" rx="4" ry="4" width="140" height="25" />    */}
        <rect x="180" y="00" rx="4" ry="4" width="140" height="25" />
        <rect x="180" y="30" rx="4" ry="4" width="140" height="25" />
        <rect x="180" y="85" rx="4" ry="4" width="140" height="25" />
        <rect x="180" y="115" rx="4" ry="4" width="140" height="25" />
        <rect x="180" y="170" rx="4" ry="4" width="140" height="25" />
        <rect x="180" y="200" rx="4" ry="4" width="140" height="25" />
        <rect x="180" y="255" rx="4" ry="4" width="140" height="25" />
        {/* <rect x="180" y="285" rx="4" ry="4" width="140" height="25" />    */}
        {/* <rect x="180" y="340" rx="4" ry="4" width="140" height="25" />    */}
        <rect x="360" y="85" rx="4" ry="4" width="140" height="25" />
        <rect x="360" y="115" rx="4" ry="4" width="140" height="25" />
        <rect x="360" y="170" rx="4" ry="4" width="140" height="25" />
        <rect x="360" y="200" rx="4" ry="4" width="140" height="25" />
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={style.width}
      height={style.height}
      loadstyle={style.loadstyle}
    >
      <AssetsDetailsDialog
        title={"common_asset_details"}
        onClose={() => removeChild(dest)}
        loadstyle={style}
      />
    </AppDialog>,
    dest
  );
};

export const openColDefChooseDialog = (data, callback = () => {}) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <ColDefChoose
        data={data}
        title={"common_select_display"}
        onClose={() => removeChild(dest)}
        onSubmit={(d) => callback(d)}
      />
    </AppDialog>,
    dest
  );
};

export const openSCBDisclaimerDialog = (callback = () => {}) => {
  const dest = createChild();

  const handleCloseClick = () => {
    window.location.href = iTradeEnv.thirdPartyLink;
    removeChild(dest);
  };

  const handleSubmitClick = () => {
    callback();
    removeChild(dest);
  };

  ReactDOM.render(
    <AppDialog onClose={handleCloseClick}>
      <SCBDisclaimerDialog
        title={"common_important_notes"}
        onSubmit={handleSubmitClick}
      />
    </AppDialog>,
    dest
  );
};

export const openWatchListGroupDialog = (title, action, watchlistItem) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <WatchListGroupDialog
        title={action == "EDIT" ? "watchlist_rename" : "market_delete_confirm"}
        action={action}
        watchlistItem={watchlistItem}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openWatchlistContextMenu = (position, onClickMenu) => {
  let dest = createChild();

  ReactDOM.render(
    <Provider store={store}>
      <Suspense fallback={<Spinner />}>
        <WatchlistContextMenu
          position={position}
          onClose={() => removeChild(dest)}
          onClickMenu={onClickMenu}
        />
      </Suspense>
    </Provider>,
    dest
  );
};

export const openWatchlistPrompt = (position, action) => {
  let dest = createChild();

  ReactDOM.render(
    <Provider store={store}>
      <Suspense fallback={<Spinner />}>
        <WatchlistPrompt
          position={position}
          onClose={() => removeChild(dest)}
          action={action}
        />
      </Suspense>
    </Provider>,
    dest
  );
};

export const openMarkrtFinancialsDetail = (index) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <FinancialsDetailDialog
        title={"market_financial_statement"}
        index={index}
      />
    </AppDialog>,
    dest
  );
};

export const openMarketCommentReport = (nid, load, getPostList) => {
  let dest = createChild();
  const style = {
    width: "700px",
    height: "290px",
    loadstyle: (
      <>
        <rect x="0" y="0" width={115} height={20}>
          {" "}
        </rect>
        <rect x="43" y="38" width={640} height={180}>
          {" "}
        </rect>
        <rect x="495" y="245" width={85} height={20}>
          {" "}
        </rect>
        <rect x="590" y="245" width={85} height={20}>
          {" "}
        </rect>
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      loadstyle={style.loadstyle}
      width={style.width}
      height={style.height}
    >
      <CommentReportDialog
        title={"market_comment_report"}
        style={style}
        onClose={() => removeChild(dest)}
        nid={nid}
        getPostList={getPostList}
        load={load}
      />
    </AppDialog>,
    dest
  );
};

export const openMarketCommentPost = (getPostList) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <CommentPostDialog
        title={"market_post_comments"}
        onClose={() => removeChild(dest)}
        getPostList={getPostList}
      />
    </AppDialog>,
    dest
  );
};

export const openMarketCommentAllComment = (
  PostsData,
  getPostList,
  ChangeLayout
) => {
  let dest = createChild();
  const style = {
    width: 640,
    height: 800,
    loadstyle: (
      <>
        <rect x="15" y="15" width={400} height={40}></rect>
        <rect x="15" y="60" width={100} height={30}></rect>
        <rect x="15" y="100" width={480} height={140}></rect>
        <rect x="15" y="245" width={100} height={30}></rect>

        <rect x="15" y="290" width={125} height={30}></rect>

        <rect x="30" y="330" width={230} height={35}></rect>
        <rect x="70" y="370" width={370} height={30}></rect>

        <rect x="30" y="420" width={230} height={35}></rect>
        <rect x="70" y="465" width={370} height={30}></rect>

        <rect x="30" y="515" width={230} height={35}></rect>
        <rect x="70" y="560" width={370} height={30}></rect>

        <rect x="30" y="610" width={230} height={35}></rect>
        <rect x="70" y="655" width={370} height={30}></rect>

        <rect x="20" y="755" width={600} height={35}></rect>
      </>
    ),
  };
  const changeStyle = {
    width: 1280,
    height: 700,
    loadstyle: (
      <>
        <rect x="15" y="15" width={350} height={30}></rect>
        <rect x="15" y="60" width={300} height={30}></rect>
        <rect x="15" y="100" width={600} height={250}></rect>
        <rect x="15" y="360" width={100} height={100}></rect>
        <rect x="120" y="360" width={100} height={100}></rect>
        <rect x="15" y="480" width={100} height={25}></rect>

        <rect x="660" y="15" width={125} height={20}></rect>
        <rect x="670" y="45" width={230} height={45}></rect>
        <rect x="710" y="95" width={400} height={20}></rect>
        <rect x="670" y="130" width={230} height={45}></rect>
        <rect x="710" y="180" width={400} height={20}></rect>
        <rect x="670" y="215" width={230} height={45}></rect>
        <rect x="710" y="265" width={400} height={20}></rect>
        <rect x="670" y="300" width={230} height={45}></rect>
        <rect x="710" y="350" width={400} height={20}></rect>
        <rect x="670" y="385" width={230} height={45}></rect>
        <rect x="710" y="435" width={400} height={20}></rect>
        <rect x="670" y="470" width={230} height={45}></rect>
        <rect x="710" y="520" width={400} height={20}></rect>
        <rect x="660" y="660" width={600} height={30}></rect>
      </>
    ),
  };
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      width={ChangeLayout ? changeStyle.width : style.width}
      height={ChangeLayout ? changeStyle.height : style.height}
      loadstyle={ChangeLayout ? changeStyle.loadstyle : style.loadstyle}
    >
      <CommentDetailDialog
        title={"market_comment_all_comments"}
        onClose={() => removeChild(dest)}
        PostsData={PostsData}
        getPostList={getPostList}
        style={ChangeLayout ? changeStyle : style}
        ChangeLayout={ChangeLayout}
      />
    </AppDialog>,
    dest
  );
};

export const openChartDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      dialogStyle={{ width: "90%" }}
      style={{ height: "30px" }}
    >
      <Chart
        chart_id="dialog-chart"
        onClose={() => removeChild(dest)}
        title="  "
      />
    </AppDialog>,
    dest
  );
};

export const openMarketPositionProportion = () => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <PositionProportionDialog title={"market_position_proportion_title"} />
    </AppDialog>,
    dest
  );
};

export const openWatchlistPromptMobile = (position, action) => {
  let dest = createChild();

  ReactDOM.render(
    <Provider store={store}>
      <Suspense fallback={<Spinner />}>
        <WatchlistPromptMobile
          position={position}
          onClose={() => removeChild(dest)}
          action={action}
        />
      </Suspense>
    </Provider>,
    dest
  );
};
export const openMarketSetDialog = (
  handleRefreshTable,
  subTabValue,
  handleChangeSubTab
) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog
      onClose={() => {
        removeChild(dest), handleRefreshTable();
      }}
    >
      <MarketSetDialog
        title={"market_custom_watchlist_management"}
        handleRefreshTable={handleRefreshTable}
        subTabValue={subTabValue}
        handleChangeSubTab={handleChangeSubTab}
      />
    </AppDialog>,
    dest
  );
};

export const openMarketWatchListConfirmDialog = (item, fun, content, title) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <MarketSetconfirmDialog
        title={title ? title : "common_confirm"}
        onClose={() => removeChild(dest)}
        name={item}
        fun={fun}
        content={content}
      />
    </AppDialog>,
    dest
  );
};

export const openMarketAddWatchlistDialog = (
  rows,
  getWatchlistAndCloseCheckBoxfun,
  subTab
) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <MarketAddWatchlistDialog
        title={"market_select_watchlist_group"}
        onClose={() => removeChild(dest)}
        rows={rows}
        getWatchlistAndCloseCheckBoxfun={getWatchlistAndCloseCheckBoxfun}
        subTab={subTab}
      />
    </AppDialog>,
    dest
  );
};

export const openVirtualTradingPlaceOrderDialog = (
  PlaceOrderParams,
  dialogStyle,
  style
) => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog
      onClose={() => removeChild(dest)}
      dialogStyle={dialogStyle}
      style={style}
    >
      <VirtualTradingPlaceOrderDialog
        title={"virtual_place_order"}
        PlaceOrderParams={PlaceOrderParams}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openMarketAddGroupDialog = () => {
  let dest = createChild();

  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <MarketAddGroupDialog
        title={"mobile_add_new_watchlist_group"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openBankAccountSettingDialog = () => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <BankAccountSetting
        title={"bank_account_setting"}
        onClose={() => removeChild(dest)}
      />
    </AppDialog>,
    dest
  );
};

export const openBankSettingResult = (registerResult, message) => {
  let dest = createChild();
  ReactDOM.render(
    <AppDialog onClose={() => removeChild(dest)}>
      <BankSettingResult
        title={"ipo_apply_result"}
        onClose={() => removeChild(dest)}
        registerResult={registerResult}
        message={message}
      />
    </AppDialog>,
    dest
  );
};

export const openOTPverificationDialog = (isMobile, params, callback = () => {}) => {
	const dest = createChild();

	function removeprevChild() {
		const parent = document.querySelectorAll("div[class*='dialog-container'");
		if (parent.length === 1) {
			parent[0].parentNode.remove();
			return;
		}
		if (isMobile && document.getElementsByClassName("otp-dialog-success").length) {
			const mParent = document.getElementsByClassName("mobile-sub-layout-open")[1];
			mParent.parentNode.remove();
		}
		for (let i = 0; i < parent.length; i++) {
			parent[i].parentNode.remove();
		}
	}

	function handleClose(otpCode = "") {
		// callback(otpCode);
		removeChild(dest);
	}

	ReactDOM.render(
		<AppDialog onClose={() => removeprevChild()}>
			<OTPverificationDialog
				title={params.showResultPage ? "edda_otp_result_title" : "common_otp_verifcation"}
				onClose={(otpCode) => handleClose(otpCode)}
				isMobile={isMobile}
				params={params}
			/>
		</AppDialog>,
		dest
	);
};

export const openEDDARegistrationDialog = () => {
	const dest = createChild();
	const style = {
		width: "99%",
		height: "99%",
		loadstyle: (
			<>
				<rect x="0" y="0" rx="4" ry="4" width="100" height="30" />
				<rect x="210" y="0" rx="4" ry="4" width="380" height="30" />
				<rect x="0" y="50" rx="4" ry="4" width="100" height="30" />
				<rect x="210" y="50" rx="4" ry="4" width="380" height="30" />
				<rect x="0" y="100" rx="4" ry="4" width="100" height="30" />
				<rect x="210" y="100" rx="4" ry="4" width="380" height="30" />
				<rect x="0" y="150" rx="4" ry="4" width="100" height="30" />
				<rect x="210" y="150" rx="4" ry="4" width="380" height="30" />
				<rect x="0" y="200" rx="4" ry="4" width="100" height="30" />
				<rect x="210" y="200" rx="4" ry="4" width="380" height="30" />
				<rect x="0" y="250" rx="4" ry="4" width="100" height="30" />
				<rect x="210" y="250" rx="4" ry="4" width="380" height="30" />
				<rect x="200" y="350" rx="4" ry="4" width="90" height="30" />
				<rect x="320" y="350" rx="4" ry="4" width="90" height="30" />
			</>
		),
	};

	ReactDOM.render(
		<AppDialog
			onClose={() => removeChild(dest)}
			dialogStyle={{ width: "700px", height: "450px" }}
			width={style.width}
			height={style.height}
			loadstyle={style.loadstyle}
			type={LOADING_TYPE.SKELETON}
		>
			<EDDAccountDialog title="edda_registration_title" onClose={() => removeChild(dest)} loadstyle={style} />
		</AppDialog>,
		dest
	);
};